import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  HomeIcon,
  CalendarIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';

import { selectAuthIsForecaster } from '../slices/authSlice';
import useMarketsOverview from '../hooks/useMarketsOverview';

const TABS = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Forecasts', href: '/forecasts', icon: CalendarIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'Schedule', href: '/schedule', icon: CalendarDaysIcon },
];

// Fixed bottom tab bar for forecasters on phones. Hidden at lg and up where
// the sidebar rail takes over; "More" opens the More sheet (MobileMoreSheet)
// with the remaining destinations (Resources, Files, Settings, Help).
export default function MobileTabBar({ onOpenMenu }) {
  const isForecaster = useSelector(selectAuthIsForecaster);
  // Called before the isForecaster guard below: hooks cannot follow an early
  // return, and useMarketsOverview no-ops (returns null) for non-forecasters.
  const overview = useMarketsOverview();
  const due = overview && !overview.closed ? overview.dueForecastCount : 0;

  if (!isForecaster) return null;

  const itemBase =
    'flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500';
  const pill = (active) =>
    `relative flex h-7 w-14 items-center justify-center rounded-full transition-colors ${
      active ? 'bg-brand-100 dark:bg-brand-900/50' : ''
    }`;

  return (
    <nav
      aria-label="Primary"
      className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex">
        {TABS.map((tab) => (
          <NavLink
            key={tab.name}
            to={tab.href}
            className={({ isActive }) =>
              `${itemBase} ${
                isActive
                  ? 'text-brand-700 dark:text-brand-300 font-semibold'
                  : 'text-gray-500 dark:text-gray-400'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={pill(isActive)}>
                  <tab.icon className="h-[22px] w-[22px]" aria-hidden="true" />
                  {tab.href === '/dashboard' && due > 0 && (
                    <span
                      aria-label={`${due} forecast${due === 1 ? '' : 's'} due`}
                      className="absolute -top-1 right-2 flex h-[17px] min-w-[17px] items-center justify-center rounded-full border-2 border-surface bg-amber-700 px-1 text-[10px] font-bold text-white dark:border-gray-870"
                    >
                      {due}
                    </span>
                  )}
                </span>
                {tab.name}
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={onOpenMenu}
          className={`${itemBase} text-gray-500 dark:text-gray-400`}
        >
          <span className={pill(false)}>
            <EllipsisHorizontalIcon className="h-[22px] w-[22px]" aria-hidden="true" />
          </span>
          More
        </button>
      </div>
    </nav>
  );
}
